import React from 'react';
import { PATHS } from 'src/common/constants';
import Button from 'src/components/Button/Button';
import FontAwesomeIcon from 'src/components/Icon/FontAwesomeIcon';
import StatusIcon from 'src/components/StatusIcon/StatusIcon';
import CalcomBookingStatus from 'src/pages/Calendar/CalcomIntegration/components/CalcomBookingStatus/CalcomBookingStatus';
import { useBranding } from 'src/branding/BrandingContextProvider';
import {
  ICalendarEventData,
  KEY_PICKUP_EVENT_TYPE,
  WALKTHROUGH_EVENT_TYPE,
} from 'src/pages/Calendar/types';
import { resolveEventTypeLabel } from '../../helpers';
import ServiceProviderCard from '../ServiceProviderCard/ServiceProviderCard';
import './event-information.scss';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const formatDateTime = (date: Date): string =>
  date.toLocaleString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

interface IEventInformationProps {
  originalEvent: ICalendarEventData;
  showViewUnitButton?: boolean;
}

const KeyPickupEventInformation = ({
  originalEvent,
  showViewUnitButton = true,
}: IEventInformationProps) => {
  const { branding } = useBranding();

  const isCalcom: boolean = originalEvent.flow === 'Calcom';

  const canViewUnit: boolean = Boolean(
    originalEvent.propertyId &&
    originalEvent.suiteId &&
    originalEvent.suiteId !== EMPTY_GUID &&
    originalEvent.number
  );

  const unitNumber: number | string = originalEvent.number || 'Unknown Unit';

  const unitUrl: string = `${window.location.origin}${PATHS.SUITES_VIEW.replace(
    ':id',
    `${originalEvent.propertyId}&${originalEvent.suiteId}`
  )}`;

  const hasServiceProvider: string =
    originalEvent.serviceProviderName ||
    originalEvent.serviceProviderCompanyName ||
    originalEvent.serviceProviderEmail ||
    originalEvent.serviceProviderPhone;

  return (
    <div className="event-information">
      <h3>Event Information</h3>
      <div className="event-info-grid">
        <div className="event-info-item">
          <div className="event-info-label">Property</div>
          <div className="event-info-value">
            {originalEvent.propertyName || 'N/A'}
          </div>
        </div>

        <div className="event-info-item">
          <div className="event-info-label">Unit</div>
          <div className="event-info-value">{unitNumber}</div>
          {canViewUnit && showViewUnitButton && (
            <a href={unitUrl} target="_blank" rel="noopener noreferrer">
              <Button
                type="button"
                color="teal"
                outline="teal"
                size="small"
                label="View unit"
                icon={
                  <FontAwesomeIcon
                    glyph="external-link"
                    weight="light"
                    size="small"
                  />
                }
              />
            </a>
          )}
        </div>

        <div className="event-info-item">
          <div className="event-info-label">Type</div>
          <div className="event-info-value">
            {resolveEventTypeLabel(originalEvent.juniperEventType)}
          </div>
        </div>
        <div className="event-info-item">
          <div className="event-info-label">Date and Time</div>
          <div className="event-info-value">
            {formatDateTime(new Date(originalEvent.eventDateTime))}
          </div>
        </div>

        {originalEvent.juniperEventType === WALKTHROUGH_EVENT_TYPE &&
          (isCalcom ? (
            <div className="event-info-item">
              <CalcomBookingStatus
                calcomBookingUid={originalEvent.calcomBookingUid}
                status={originalEvent?.status}
              />
            </div>
          ) : (
            <div className="event-info-item">
              <div className="event-info-label">Status</div>
              <div className="event-info-item">
                <StatusIcon
                  status={originalEvent?.status}
                  showLabel={true}
                  size="small"
                  className="provider-status"
                />
              </div>
            </div>
          ))}

        {originalEvent.juniperEventType === KEY_PICKUP_EVENT_TYPE && (
          <div className="event-info-item">
            <CalcomBookingStatus
              calcomBookingUid={originalEvent.calcomBookingUid}
              status={originalEvent?.status}
            />
          </div>
        )}

        <div className="event-info-item">
          <div className="event-info-label">{branding.text.homeOwner}</div>
          <div className="event-info-value">
            {originalEvent.homeOwner || 'N/A'}
          </div>
        </div>

        {/* Show Days Since Submitted when available (typically for service requests). */}
        {originalEvent.daysSinceSubmitted !== null &&
          originalEvent.daysSinceSubmitted !== undefined && (
            <div className="event-info-item">
              <div className="event-info-label">Days Since Submitted</div>
              <div className="event-info-value">
                {originalEvent.daysSinceSubmitted} days
              </div>
            </div>
          )}
      </div>

      {hasServiceProvider && (
        <div className="service-provider-section">
          <h4>Assigned Service Provider</h4>
          <ServiceProviderCard
            companyName={originalEvent.serviceProviderCompanyName}
            name={originalEvent.serviceProviderName}
            email={originalEvent.serviceProviderEmail}
            phone={originalEvent.serviceProviderPhone}
            originalEvent={originalEvent}
          />
        </div>
      )}
    </div>
  );
};

export default KeyPickupEventInformation;
